<template>
  <div>
    <v-data-table
      :headers="tableHeaders"
      :items="tableData"
      hide-actions
      :hide-headers="hideHeaders"
      class="text-xs-center"
      :class="tableClass"
    >
      <template v-if="customHeader" v-slot:headers="props">
        <tr>
          <template v-for="columnName in props.headers">
            <th
              v-if="!columnName.sortable"
              :key="columnName.value"
              class="custom-header text-xs-center"
              :class="columnName.class"
              :width="columnName.width"
              valign="bottom"
            >
              <template
                v-if="columnName.type && columnName.type === 'checkbox'"
              >
                <div
                  class="header-checkbox text-xs-center"
                  @click="$emit('action-clicked', columnName.selected)"
                >
                  <v-checkbox v-model="columnName.selected" hide-details />
                </div>
                {{ columnName.text }}
              </template>
              <div v-else>
                {{ columnName.text }}
              </div>
            </th>
            <th
              v-else
              :key="columnName.value"
              class="custom-header text-xs-center"
              :width="columnName.width"
              valign="bottom"
              :class="[
                'column sortable',
                pagination.descending ? 'desc' : 'asc',
                columnName.value === pagination.sortBy ? 'active' : '',
                ...columnName.class
              ]"
              @click="changeSort(columnName.value)"
            >
              {{ columnName.text }}
              <v-icon small>
                expand_less
              </v-icon>
            </th>
          </template>
        </tr>
      </template>
      <template v-slot:items="props">
        <td
          v-for="(item, index) in props.item"
          :key="index"
          :class="item.cellClass"
        >
          <div
            v-if="item.type === 'avatar'"
            :class="getMatchPercentClass(item)"
            class="clickable hwayzTeal text-capitalize wide name-wrapping"
            :style="{ color: [item.color] }"
            @click.stop="emitClick(item)"
          >
            <v-avatar
              size="30px"
              style="margin-right: 15px"
              :color="colors[item.text[0]]"
            >
              <div>
                <span class="white--text">{{ item.text | initials }}</span>
              </div>
            </v-avatar>
            {{
              item.data && item.data.type === "match"
                ? roundOffMatch(item.text)
                : item.text
            }}
          </div>

          <div v-else-if="item.type === 'multiple'">
            <div
              v-for="dataObject in item.dataArray"
              :key="dataObject.text"
              :class="getMatchPercentClass(dataObject)"
              class="clickable"
              @click.stop="emitClick(dataObject)"
            >
              {{
                dataObject.data && dataObject.data.type === "match"
                  ? roundOffMatch(dataObject.text)
                  : dataObject.text
              }}
            </div>
          </div>

          <div
            v-else-if="item.type === 'clickable'"
            :class="getMatchPercentClass(item)"
            class="clickable"
            :style="{ color: [item.color] }"
            @click.stop="emitClick(item)"
          >
            {{
              item.data && item.data.type === "match"
                ? roundOffMatch(item.text)
                : item.text
            }}
          </div>

          <div v-else-if="item.type === 'button'">
            <v-btn
              round
              dark
              :outline="item.outline"
              :color="item.color"
              :class="item.class"
              @click="emitClick(item)"
            >
              {{ item.text }}
            </v-btn>
          </div>

          <div v-else-if="item.type === 'clickableIcon'">
            <v-btn
              fab
              small
              :class="[item.btnClass, 'fabSmall']"
              @click.stop="emitClick(item)"
            >
              <v-icon small>
                {{ item.text }}
              </v-icon>
            </v-btn>
          </div>
          <div
            v-else-if="item.type === 'clickableText'"
            :class="[item.class, 'resend-invite-btn']"
            :style="{ color: [item.color] }"
            class="clickable"
            @click.stop="emitClick(item)"
          >
            {{ item.text }}
          </div>
          <div v-else-if="item.type === 'icon'" :class="item.class">
            <v-icon
              :color="item.color"
              class="thin"
              @click.stop="emitClick(item)"
            >
              {{ item.text }}
            </v-icon>
          </div>

          <div
            v-else-if="item.type === 'address'"
            class="feedback-address"
            @click.stop="showPropertyModal(true, item.data)"
          >
            <v-icon v-if="item.icon" small>
              place
            </v-icon>
            <div v-if="item.preText" :class="[item.preTextClass, 'pre-text']">
              {{ item.preText }}&nbsp;
            </div>
            <span>{{ item.text }}</span>
          </div>

          <template v-else-if="item.type === 'route-address'">
            <div
              class="route-details-notification-item"
              :class="[item.class]"
            >
              <v-icon v-if="item.icon" small>
                place
              </v-icon>
              <div v-if="item.preText" :class="[item.preTextClass, 'pre-text']">
                {{ item.preText }}&nbsp;
              </div>
              <span
                :class="{ 'route-address-hover': item.data.propertyId }"
                @click.stop="
                  item.data.propertyId
                    ? showPropertyModal(true, item.data)
                    : null"
              >{{ item.text }}</span>
            </div>
            <div
              v-if="displayMessage(item)"
              refs="routeAddress"
              class="route-details-notification-message"
            >
              <v-icon class="route-details-notification">
                error
              </v-icon>
              <div>
                {{ item.data.agent_message }}
              </div>
            </div>
          </template>

          <div v-else-if="item.type === 'date'">
            {{ item.text | dateFormat }}
          </div>

          <div
            v-else-if="item.type === 'addressImg'"
            class="address-image"
            :class="item.class"
            @mouseenter="hoverRow = item.data.propertyId"
            @mouseleave="hoverRow = null"
            @click.stop="
              getFullAddress(item.data)
                ? showPropertyModal(true, item.data)
                : null"
          >
            <template v-if="getFullAddress(item.data)">
              <div v-ripple class="img-wrap">
                <img
                  :src="getHomeDetails(item.data, 'Images')"
                  class="home-image"
                  height="100%"
                  width="100%"
                  :aspect-ratio="16 / 10"
                >
                <v-layout
                  v-if="hoverRow === item.data.propertyId"
                  class="img-text"
                  align-center
                  justify-center
                >
                  <v-flex xs12>
                    View Details
                  </v-flex>
                </v-layout>
              </div>
              <span>{{ getFullAddress(item.data) }}</span>
            </template>
            <template v-else>
              <row-loading-animation column-type="addressImg" />
            </template>
          </div>

          <div
            v-else-if="item.type === 'picIndicator'"
            class="indicator-pictures"
            :class="{ 'indicator-pictures--true': item.picStatus }"
          />

          <div
            v-else-if="item.type === 'checkbox'"
            class="isCheckbox"
            :class="item.class"
            @click="checkboxClick(item.selected, props)"
          >
            <v-checkbox v-model="item.selected" :disabled="item.disabled" />
          </div>

          <div v-else :class="[item.class]">
            <v-icon
              v-if="item.icon"
              :class="[item.iconClass]"
              :size="item.iconSize"
            >
              {{ item.icon }}
            </v-icon>
            <div v-if="item.preText" :class="[item.preTextClass, 'pre-text']">
              {{ item.preText }}&nbsp;
            </div>
            {{ item.text }}
          </div>
        </td>
      </template>
    </v-data-table>
    <detail-property-view
      v-if="displayPropertyModal && isMobile"
      class="mobile-calendar-property-view"
      :property-object-id="selectedProperty._id"
    />
    <property-view-modal
      v-else
      :show-modal="displayPropertyModal"
      :home-info="selectedProperty"
      @close-modal="showPropertyModal($event)"
    />
  </div>
</template>

<script>
import moment from 'moment'
import { mapState } from 'vuex'
import {
  calculateFeedbackMatch,
  getPropertyKeyValue,
  propertyFullAddress,
  getPropertyImage
} from '../../utils/propertyUtils'
import { isEmpty } from '../../utils/generalUtils'
import PropertyViewModal from '../modals/PropertyViewModal.vue'
import DetailPropertyView from '../search/property_view/DetailPropertyView'
import RowLoadingAnimation from './RowLoadingAnimation.vue'
import avatarColors from '../../utils/avatarColors'
import { getPropertyById } from '../../services/propertiesService'

export default {
  components: { PropertyViewModal, RowLoadingAnimation, DetailPropertyView },
  filters: {
    dateFormat(value) {
      return moment(value).format('MM-DD-YY')
    },
    initials: function(value) {
      if (!value) return ''
      value = value.split(' ')
      let response = ''
      for (const v of value) {
        response = response + v.charAt(0).toUpperCase()
      }
      return response
    }
  },

  props: {
    tableHeaders: {
      type: Array,
      default: () => []
    },
    tableData: {
      type: Array,
      required: true
    },
    tableClass: {
      type: String,
      required: false,
      default: ''
    },
    customHeader: {
      type: Boolean,
      default: false
    },
    hideHeaders: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      hoverRow: null,
      displayPropertyModal: false,
      selectedProperty: null,
      colors: avatarColors,
      pagination: {
        sortBy: null
      }
    }
  },

  computed: {
    ...mapState('client', ['clientFavorites']),
    ...mapState('common', ['profileStatus', 'isMobile'])
  },

  methods: {
    emitClick(item) {
      const { data } = item
      if (data) {
        if (data.action) {
          this.$emit(data.action, data.value)
        } else {
          this.$emit('clicked', data)
        }
      } else {
        this.$emit('clicked')
      }
    },

    displayMessage(item) {
      return item.data.agent_message && this.profileStatus === 'agent'
    },

    changeSort(column) {
      if (this.pagination.sortBy === column) {
        this.pagination.descending = !this.pagination.descending
      } else {
        this.pagination.sortBy = column
        this.pagination.descending = false
      }
      this.$emit('sort-changed', this.pagination)
    },

    calculateFeedbackPercent(home) {
      if (!isEmpty(home.feedback)) {
        return calculateFeedbackMatch(home.feedback.match)
      }
      return 'N/A'
    },

    getMatchPercentClass(item) {
      if (item.data && item.data.type === 'match') {
        const percentValue = item.data.value
        let percentClass = ''
        if (this.calculateFeedbackPercent(percentValue) / 10 >= 8) {
          percentClass = 'hwayzGreen'
        } else if (this.calculateFeedbackPercent(percentValue) / 10 >= 6) {
          percentClass = 'hwayzSun'
        } else {
          percentClass = 'color-grey'
        }
        return [percentClass, 'matchClass']
      } else if (item.class) {
        return item.class
      }
      return null
    },

    roundOffMatch(value) {
      return Math.round(value) + '%'
    },

    async showPropertyModal(status, property = null) {
      this.displayPropertyModal = status
      let propertyObject = null
      if (status) {
        propertyObject = this.clientFavorites.find(
          home => home._id === property.propertyId
        )
        if (!propertyObject) {
          propertyObject = (await getPropertyById(property.propertyId)).data
        }
      }
      this.selectedProperty = { ...propertyObject }
    },

    checkboxClick(selected, item) {
      this.$emit('checkboxClicked', { selected, item })
    },

    /**
     * Retrieve home details based on property and required value
     * Calls the common utility function for retrieving a key value for property
     *
     * @public
     * @param {Object} home home object of selected home
     * @param {String} key specific detail for the property
     */
    getHomeDetails(home, key) {
      if (key === 'Images' && home['Images']) {
        return getPropertyImage(home)
      }
      return getPropertyKeyValue(home.propertyId, this.clientFavorites, key)
    },

    getFullAddress(data) {
      const propertyObject = this.clientFavorites.find(
        home => home._id === data.propertyId
      )
      if (propertyObject) {
        this.$emit('property-loaded', true)
        return propertyFullAddress(propertyObject)
      } else if (data.Address) {
        this.$emit('property-loaded', true)
        return propertyFullAddress(data)
      } else {
        return false
      }
    }
  }
}
</script>

<style lang="sass" scoped>
.route-address-hover
  cursor: pointer
  &:hover
    color: $hwayzHover

.v-avatar
  flex: 0 0 30px !important
</style>
<style lang="sass">

.feedback-address
  text-align: left !important
  i
    padding-right: 1rem
  span
    cursor: pointer
  span:hover
    color: $hwayzHover

.address-image
  text-align: left !important
  padding-top: 0.8rem
  padding-bottom: 0.8rem
  height: 90px
  display: flex
  align-items: center
  .img-wrap
    min-width: 120px
    max-width: 120px
    width: 120px
  span
    cursor: pointer
    &:hover
      color: $hwayzHover
  @media only screen and (max-width: 360px)
    height: 75px
    .img-wrap
      min-width: 100px
      max-width: 100px
    span
      font-size: 1.4rem

.img-wrap
  cursor: pointer
  position: relative
  width: 25%
  max-width: 14rem
  min-width: 10rem
  height: 100%
  display: inline-block
  vertical-align: middle
  margin-right: 1rem
  float: left

.img-text
  position: absolute
  top: 0
  left: 0
  height: 100%
  width: 100%
  border-radius: 0.5rem
  background-color: rgba($hwayzBlack, 0.4)
  color: white
  text-align: center
  margin: 0 auto !important

.home-image
  border-radius: 0.5rem

.indicator-pictures
  width: 1.2rem
  height: 1.2rem
  border-radius: 50%
  margin: 0 auto

  border: 0.1rem solid $hwayzOrange
  background-color: white

.indicator-pictures--true
  border: 0.1rem solid $hwayzDarkenGreen
  background-color: $hwayzDarkenGreen

.clickable
  cursor: pointer
  // text-transform: uppercase

.fab-icon
  height: 2.4rem !important
  width: 2.4rem !important

.pre-text
  margin-left: -2rem
  display: inline

.isCheckbox
  justify-self: center !important
  position: relative
  top: 50%
  left: 50%
  transform: translate(-5%, -25%)

.header-checkbox
  position: absolute
  top: 25%
  left: 50%
  transform: translate(-5%, -25%)

.custom-header
  position: relative
  height: 8rem
  padding-bottom: 16px !important

.table-no-border table.v-table tbody tr
  border-bottom: none !important

.vertical-align-top td
  vertical-align: top !important
  padding-top: 14px !important

td.agent-message
  vertical-align: top !important
  padding-top: 8px !important

.no-overflow
  .v-table__overflow, .v-datatable, .v-table
    overflow-y: visible !important

.route-details-notification-item
  position: relative

.route-details-notification-message
  width: 170%
  display: flex
  align-items: center
  margin: 2rem 0
  border-radius: 15px
  padding: 5px 10px
  z-index: 1
  background: $hwayzLightGray
  @media only screen and (min-width: 960px)
    width: 200%
  @media only screen and (min-width: 1080px)
    width: 180%
  @media only screen and (min-width: 1264px)
    width: 150%

.route-details-notification
  color: $hwayzYellow !important
  margin-right: 8px

.textLeft
  text-align: left !important

.capitalize
  text-transform: capitalize !important

.uppercase
  text-transform: uppercase !important

.fabSmall
  width: 30px !important
  height: 30px !important

.v-datatable, .v-table
  overflow: hidden

.btnSmall
  height: 2.5rem !important
  font-size: 1.1rem !important
  &.hwayzTeal
    background-color: $hwayzTeal !important
  &.hwayzSun
    background-color: $hwayzSun !important
  &.hwayzDarkenGreen
    background-color: $hwayzDarkenGreen !important

/* Color classes - can be modified if needed
 * Using same name for class and color at the moment. */
// Define a color for grey around #999
.color-grey
  color: $hwayzMediumGray
  &.matchClass:hover
    background-color: rgba($hwayzMediumGray, .2)

.hwayzGreen
  color: $hwayzGreen
  &.matchClass:hover
    background-color: rgba($hwayzGreen, .2)

.hwayzTeal
  color: $hwayzTeal
  &.matchClass:hover
    background-color: rgba($hwayzTeal, .2)

.hwayzDarkenGreen
  color: $hwayzDarkenGreen

.hwayzSun
  color: $hwayzSun
  &.matchClass:hover
    background-color: rgba($hwayzSun, .2)

.hwayzBlue
  color: $hwayzBlue

.matchClass
  margin: 0 auto
  text-align: center
  border-radius: 50%
  width: 36px
  height: 36px
  line-height: 36px

.wide
  width: 100%
.thin
  width: 50px
.text-xs-center
  text-align: center!important
.text-xs-center.text-xs-left, .text-xs-left
  text-align: left!important
.disable
  @extend %disabled-state
.no-pointer-events
  @extend %disable-selection
  pointer-events: none
.saved
  color: $saved
.pending
  color: $hwayzYellow
.scheduled
  color: $hwayzGreen
.accept
  color: $accept
.decline
  color: $decline
.confirmed
  color: $hwayzBlue
.completed
  color: $completed
.expired
  color: $hwayzMediumGray
.custom-table-class table.v-table tbody tr:not(:last-child)
  border-bottom: none

.name-wrapping
  display: flex
  flex-wrap: nowrap
  flex-direction: row
  justify-content: flex-start
  align-items: center
@media screen and (max-width: 750px)
  .custom-table-class table.v-table tbody tr td div.v-avatar
    display: none

@media only screen and (max-width: 960px)
  .feedback-address_image
    margin: 0 -20px 0 -10px !important
.custom-table-class .v-table tbody td
  font-weight: 400 !important
  color: $hwayzBlack
.custom-table-class .v-table thead tr th
  font-weight: 500 !important
  color: $hwayzBlack

.v-datatable thead th.column.sortable .v-icon
  opacity: 0.5 !important
</style>
